import { useFormContext } from 'react-hook-form';
import type { Account } from '../types';
import { usePagination } from 'hooks/usePagination';
import { useAccounts } from '../api';
import { useAccountsColumns, useGridSelection } from '../hooks';
import { BaseGrid } from 'components/common';
import { SuspenseBoundary } from 'components/common';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { useListParams } from 'hooks';
import { debounce, isEmpty } from 'lodash-es';
import { usePermissionGuard } from '../../../login';

const AccountsGridComponent = memo(() => {
  const { accessToChangeFacebookadaccount } = usePermissionGuard();
  const { watch } = useFormContext();
  const pagination = usePagination([], 50);
  const { setQ, setApps, q: urlQ, apps: urlApps } = useListParams();
  const [debouncedFilters, setDebouncedFilters] = useState<{
    q: string | undefined;
    apps: string | undefined;
  }>({
    q: urlQ ?? undefined,
    apps: Array.isArray(urlApps) ? (urlApps[0] ?? undefined) : undefined,
  });

  const filterByQ = watch('q') as string;
  const filterByApps = watch('apps') as string[];

  const debouncedUpdate = useCallback(
    (q: string, apps?: string[]) => {
      setDebouncedFilters({ q, apps: apps?.[0] ?? undefined });
      setQ(!isEmpty(q) ? q : undefined);
      setApps(apps ?? undefined);
    },
    [setDebouncedFilters, setQ, setApps],
  );

  const debouncedFn = useMemo(
    () => debounce(debouncedUpdate, 500),
    [debouncedUpdate],
  );

  useEffect(() => {
    debouncedFn(filterByQ, filterByApps);
    return () => {
      debouncedFn.cancel();
    };
  }, [filterByQ, filterByApps, debouncedFn]);

  const { selectedRows, handleSelectionChange } =
    useGridSelection('selectedAccounts');
  const navigate = useNavigate();

  const columns = useAccountsColumns();
  const { desc } = useListParams();

  const {
    data: { data: accounts, count },
    isLoading,
  } = useAccounts({
    params: {
      ...(debouncedFilters.q && { name: debouncedFilters.q }),
      ...(debouncedFilters.apps && { apps: debouncedFilters.apps }),
      limit: pagination.perPage,
      offset: pagination.page,
      order_by: 'name',
      desc: desc,
    },
  });

  const handleClick = useCallback(
    (id: number) => {
      accessToChangeFacebookadaccount && navigate(`/cabinet/facebook/accounts/${id}`);
    },
    [navigate, accessToChangeFacebookadaccount],
  );

  return (
    <BaseGrid<Account>
      rows={accounts}
      columns={columns}
      count={count}
      isLoading={isLoading}
      pagination={pagination}
      rowSelectionModel={selectedRows}
      onRowSelectionModelChange={handleSelectionChange}
      getRowId={(row) => row.ad_account_id}
      onRowClick={(params) => {
        handleClick((params.row as Account).id);
      }}
    />
  );
});

AccountsGridComponent.displayName = 'AccountsGridComponent';

const AccountsGrid = () => {
  return (
    <SuspenseBoundary>
      <AccountsGridComponent />
    </SuspenseBoundary>
  );
};

export default AccountsGrid;
